import React, { useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ImageSourcePropType,
  ViewStyle,
  Animated,
} from 'react-native';
import { IconWithFallback } from './TokenIcon';
import { COLORS } from '../../theme/themeTokens';
import { formatTokenAmount } from '../../utils/amountFormat';

export interface AccountCardData {
  /** Icon source (e.g. from getCurrencyIcon); ignored when iconNode is set */
  icon?: ImageSourcePropType;
  /** Bundled icon used when `icon` is a URL that fails to load. */
  iconFallback?: ImageSourcePropType;
  /** Custom icon element, e.g. StockIcon for xStocks. */
  iconNode?: React.ReactNode;
  /** Account name, e.g. "Tether, USDT" */
  name: string;
  /** USDT equivalent as a number, e.g. 837.25 */
  valueUSDT: number;
  /** Account value in asset currency, e.g. 837.25 */
  value: number;
  /** Percentage change as a number, e.g. 10.25 or -5.3 */
  changePercent: number;
  /** Number of decimal places to show for the asset value. Defaults to 2 */
  decimals?: number;
  /** Reserved part of the balance, in the asset's own currency. Shown under
   *  the value as "🔒 108 443,43" — the money is the client's, but a pending
   *  request (a withdrawal, a card top-up, a deposit closure) has it held.
   *  Without the marker the tile reads as spendable and the Dashboard total,
   *  which counts only what is free, looks wrong by exactly this much. */
  locked?: number;
}

export type AccountCardColorScheme = 'dark' | 'light';

interface AccountCardProps {
  data: AccountCardData;
  /** Called when the card is pressed */
  onPress?: () => void;
  /** Card background color. Defaults to white */
  backgroundColor?: string;
  /** Text color scheme: 'dark' (for light backgrounds) | 'light' (for dark backgrounds). Defaults to 'dark' */
  colorScheme?: AccountCardColorScheme;
  /** Optional outer container style (e.g. margins) */
  style?: ViewStyle;
  /** Whether to show the USDT equivalent value. Defaults to true */
  showValueUSDT?: boolean;
  /** Whether to show the percentage change. Defaults to true */
  showChangePercent?: boolean;
  /** Optional label shown in green before the percent value (e.g. "APY") */
  percentLabel?: string;
  /** Period the percent covers, printed after it in muted type: "24h"
   *  reads as "−1,97% / 24h". The card is handed a number and cannot know
   *  what span it measures, so the caller says — the same way percentLabel
   *  works. Left out, the percent prints bare, as it always did. */
  percentSuffix?: string;
  /** Optional arbitrary subtitle shown below the name (replaces USDT value if set) */
  subtitle?: string;
  /** Skip the "no onPress" shake animation. Use on informational tiles
   *  (e.g. network rows on BalanceDetailScreen) where the user shouldn't
   *  perceive feedback on tap. */
  disableShake?: boolean;
  /** Increased vertical spacing between the two lines in each column
   *  (name↔valueUSDT and value↔changePercent). Explicit 8px marginTop
   *  на второй строке + пропорционально увеличенная высота card'а.
   *  Используется на Dashboard'e чтобы визуально "расправить" tile'ы.
   *  Прочие экраны (AllAssets / BalanceDetail / etc.) остаются на
   *  compact-версии. */
  spacious?: boolean;
}

const AccountCard: React.FC<AccountCardProps> = ({
  data,
  onPress,
  backgroundColor = COLORS.white,
  colorScheme = 'dark',
  style,
  showValueUSDT = true,
  showChangePercent = true,
  percentLabel,
  percentSuffix,
  subtitle,
  disableShake = false,
  spacious = false,
}) => {
  const isDark = colorScheme === 'dark';

  const primaryColor = isDark ? COLORS.textPrimary : COLORS.white;
  const secondaryColor = isDark ? COLORS.blueSteel : 'rgba(255,255,255,0.65)';
  const borderColor = isDark ? COLORS.borderMuted : 'transparent';

  let percentColor: string;
  let sign: string;
  if (data.changePercent > 0) {
    percentColor = COLORS.mint;
    sign = '+';
  } else if (data.changePercent < 0) {
    percentColor = COLORS.brandOrange;
    sign = '\u2013';
  } else {
    percentColor = COLORS.blueSteel;
    sign = '';
  }

  const formatNumber = (num: number, decimals = 2): string => {
    const fixed = num.toFixed(decimals);
    const [intPart, decPart] = fixed.split('.');
    return `${intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0')},${decPart}`;
  };

  // `decimals` приходит из каталога токена — это точность контракта (18 у
  // USDT, 18 у ETH), а не число цифр для человека. Потолок — в
  // formatTokenAmount. formatNumber ниже остаётся процентам: им нужен
  // ровно один вид, "1,50%", с нулём в хвосте.
  const formattedValue = formatTokenAmount(data.value, data.decimals ?? 2);

  // A held balance outranks the day's move: the percent is context, the lock
  // answers "why can't I spend this". They share the one line under the
  // value, so only one of them can be there.
  const formattedLocked = data.locked && data.locked > 0
    ? `\u{1F512} ${formatTokenAmount(data.locked, data.decimals ?? 2)}`
    : null;

  const absPercent = formatNumber(Math.abs(data.changePercent));
  const formattedPercent = data.changePercent === 0
    ? `${absPercent}%`
    : `${sign}${absPercent}%`;

  const shakeAnim = useRef(new Animated.Value(0)).current;

  const triggerShake = () => {
    Animated.sequence([
      Animated.timing(shakeAnim, { toValue: 6,  duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnim, { toValue: -6, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnim, { toValue: 5,  duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnim, { toValue: -5, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnim, { toValue: 3,  duration: 40, useNativeDriver: true }),
      Animated.timing(shakeAnim, { toValue: 0,  duration: 40, useNativeDriver: true }),
    ]).start();
  };

  const handlePress = () => {
    if (!onPress) {
      if (!disableShake) triggerShake();
      return;
    }
    onPress();
  };

  // spacious: явные 8px marginTop на второй строке каждой колонки
  // (name↔valueUSDT, value↔changePercent) + card height растёт на 8
  // (с 66 до 74) чтобы content не clip'нулся. Compact-режим оставляет
  // прежние 0 marginTop и height:66. Возвращаем styles inline
  // (StyleSheet статичен, conditional-стили строим здесь).
  const spaciousSecondaryLine = spacious ? { marginTop: 8 } : null;
  const spaciousCardHeight = spacious ? { height: 74 } : null;

  return (
    <Animated.View style={[{ transform: [{ translateX: shakeAnim }] }, style]}>
      <TouchableOpacity
        style={[styles.card, spaciousCardHeight, { backgroundColor, borderColor }]}
        onPress={handlePress}
        activeOpacity={onPress ? 0.75 : 1}
      >
      {data.iconNode ? (
        <View style={styles.iconSlot}>{data.iconNode}</View>
      ) : (
        <IconWithFallback source={data.icon} fallback={data.iconFallback} style={styles.icon} />
      )}

      {/* Left column: name + USDT value */}
      <View style={styles.leftCol}>
        <Text style={[styles.name, (showValueUSDT || !!subtitle) && styles.nameFlex, { color: primaryColor }]} numberOfLines={1}>
          {data.name}
        </Text>
        {subtitle ? (
          <Text style={[styles.valueUSDT, spaciousSecondaryLine, { color: secondaryColor }]} numberOfLines={1}>
            {subtitle}
          </Text>
        ) : showValueUSDT ? (
          <Text style={[styles.valueUSDT, spaciousSecondaryLine, { color: secondaryColor }]} numberOfLines={1}>
            {`${formatNumber(data.valueUSDT)} USDT`}
          </Text>
        ) : null}
      </View>

      {/* Right column: asset value + percent */}
      <View style={styles.rightCol}>
        <Text style={[styles.value, (showChangePercent || !!formattedLocked) && styles.valueFlex, { color: primaryColor }]} numberOfLines={1}>
          {formattedValue}
        </Text>
        {formattedLocked ? (
          <Text style={[styles.changePercent, spaciousSecondaryLine, { color: secondaryColor }]} numberOfLines={1}>
            {formattedLocked}
          </Text>
        ) : showChangePercent && (
          <Text style={[styles.changePercent, spaciousSecondaryLine, { color: percentColor }]} numberOfLines={1}>
            {percentLabel ? (
              <Text style={{ color: COLORS.mint }}>{percentLabel + ' '}</Text>
            ) : null}{formattedPercent}{percentSuffix ? (
              <Text style={{ color: secondaryColor }}>{' / ' + percentSuffix}</Text>
            ) : null}
          </Text>
        )}
      </View>
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderWidth: 1,
  },
  icon: {
    width: 32,
    height: 32,
    marginRight: 10,
  },
  iconSlot: {
    marginRight: 10,
  },
  leftCol: {
    flex: 1,
    justifyContent: 'center',
  },
  rightCol: {
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  name: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
  },
  nameFlex: {
    flex: 1,
  },
  valueUSDT: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
  },
  value: {
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
  },
  valueFlex: {
    flex: 1,
  },
  changePercent: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
  },
});

export default AccountCard;
